//
// # EllipseEntity
//

import { model, graphics, LineSegment } from "GenAlphaModel";
import { EntityKind } from "../entity";
import { MousePos } from "../mousepos";
import { DrawTo } from "../draw";
import { ShapeBase } from "../tree";

const EDGE_HIT_TOLERANCE = 5;

export class EllipseEntity extends ShapeBase<graphics.Ellipse> {
  readonly entityKind = EntityKind.Ellipse;

  override redraw = (to: DrawTo): unknown => {
    return to.ellipse(this.data, this.style);
  };

  get body(): EllipseBody {
    return EllipseBody.new(this);
  }

  // The four bbox corners, as draggable resize handles (a la RectEntity).
  get corners(): [EllipseCorner, EllipseCorner, EllipseCorner, EllipseCorner] {
    const { bbox } = this.data;
    const opposites = [
      [bbox.min, bbox.max],
      [
        { x: bbox.min.x, y: bbox.max.y },
        { x: bbox.max.x, y: bbox.min.y },
      ],
    ];
    return [
      new EllipseCorner(this, opposites[0][0], opposites[0][1]),
      new EllipseCorner(this, opposites[0][1], opposites[0][0]),
      new EllipseCorner(this, opposites[1][0], opposites[1][1]),
      new EllipseCorner(this, opposites[1][1], opposites[1][0]),
    ];
  }

  // Current center of the ellipse.
  get loc(): model.Point {
    return model.Bbox.sizeAndCenter(this.data.bbox).center;
  }

  // Translate the ellipse to a new center location.
  updateLoc = (loc: model.Point): unknown => {
    const { size } = model.Bbox.sizeAndCenter(this.data.bbox);
    return this.update({ bbox: model.Bbox.fromSizeAndCenter(size, loc) });
  };

  // Hit test against the four bbox edges (for whole-shape movement).
  edgeHitTest = (mousePos: MousePos): boolean => {
    const { bbox } = this.data;
    const corners = [
      bbox.min,
      { x: bbox.max.x, y: bbox.min.y },
      bbox.max,
      { x: bbox.min.x, y: bbox.max.y },
    ];
    for (let i = 0; i < corners.length; i++) {
      const next = corners[(i + 1) % corners.length];
      const seg: LineSegment = { ends: [corners[i], next] };
      if (LineSegment.closest(mousePos.sch, seg).dist < EDGE_HIT_TOLERANCE) {
        return true;
      }
    }
    return false;
  };
}

export class EllipseBody {
  readonly entityKind = EntityKind.EllipseBody;
  private constructor(
    readonly parent: EllipseEntity,
    readonly size: model.Size,
    public loc: model.Point
  ) {}
  static new(parent: EllipseEntity): EllipseBody {
    const { size, center } = model.Bbox.sizeAndCenter(parent.data.bbox);
    return new EllipseBody(parent, size, center);
  }
  hitTest = (mousePos: MousePos): boolean => {
    return model.Bbox.contains(this.parent.data.bbox, mousePos.sch);
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
  update = (data: { loc: model.Point }) => {
    const { loc } = data;
    const bbox = model.Bbox.fromSizeAndCenter(this.size, loc);
    this.parent.update({ bbox });
    this.loc = loc;
  };
}

// A draggable corner handle that resizes the ellipse's bbox.
// Mirrors RectCorner: dragging `loc` reshapes the bbox while the
// diagonally-opposite corner stays fixed.
export class EllipseCorner {
  readonly entityKind = EntityKind.EllipseCorner;

  constructor(
    public parent: EllipseEntity,
    public loc: model.Point,
    public opposite: model.Point
  ) {}

  hitTest = (mousePos: MousePos): boolean => {
    return model.Point.dist(mousePos.sch, this.loc) < 5;
  };

  update = (data: { loc: model.Point }) => {
    const { loc } = data;
    this.parent.update({ bbox: model.Bbox.fromPoints(loc, this.opposite) });
    this.loc = loc;
  };
  redraw = (to: DrawTo) => this.parent.redraw(to);
  highlight = (state: boolean) => this.parent.highlight(state);
}
